/**
 * Centralized error translation utility.
 * Maps backend error keys to i18n translation paths.
 * Falls back gracefully if no translation is found.
 */

const ERROR_KEY_MAP = {
  // Auth
  invalid_credentials: 'errors.invalidCredentials',
  email_not_verified: 'errors.emailNotVerified',
  account_deactivated: 'errors.accountDeactivated',
  email_already_exists: 'errors.emailAlreadyExists',
  company_name_required: 'errors.companyNameRequired',
  drivers_only: 'errors.driversOnly',
  already_linked: 'errors.alreadyLinked',
  company_code_not_found: 'errors.companyCodeNotFound',
  already_linked_to_company: 'errors.alreadyLinkedToCompany',
  admins_only: 'errors.adminsOnly',
  no_company_found: 'errors.noCompanyFound',
  company_not_found: 'errors.companyNotFound',
  owner_drivers_only: 'errors.ownerDriversOnly',
  company_not_linked: 'errors.companyNotLinked',
  preview_only: 'errors.previewOnly',
  email_required: 'errors.emailRequired',
  account_not_found: 'errors.accountNotFound',
  invalid_reset_token: 'errors.invalidResetToken',
  reset_link_expired: 'errors.resetLinkExpired',
  reset_link_used: 'errors.resetLinkUsed',
  user_not_found: 'errors.userNotFound',
  images_only: 'errors.imagesOnly',
  super_admin_only: 'errors.superAdminOnly',
  incident_not_found: 'errors.incidentNotFound',
  invalid_status: 'errors.invalidStatus',
  current_password_incorrect: 'errors.currentPasswordIncorrect',
  password_too_short: 'errors.passwordTooShort',
  password_must_differ: 'errors.passwordMustDiffer',
  not_found: 'errors.notFound',
  resend_cooldown: 'errors.resend_cooldown',

  // CMR
  subscription_required: 'errors.subscriptionRequired',
  driver_not_found: 'errors.driverNotFound',
  driver_name_required: 'errors.driverNameRequired',
  cmr_not_found: 'errors.cmrNotFound',
  cmr_access_denied: 'errors.cmrAccessDenied',
  token_expired: 'errors.tokenExpired',
  invalid_token: 'errors.invalidToken',
  receiver_must_sign: 'errors.receiverMustSign',
  drivers_send_only: 'errors.driversSendOnly',
  cmr_send_after_completion: 'errors.cmrSendAfterCompletion',
  cmr_send_all_signatures: 'errors.cmrSendAllSignatures',
  email_send_failed: 'errors.emailSendFailed',
  planners_admins_only: 'errors.plannersAdminsOnly',
  assigned_cmr_no_delete: 'errors.assignedCmrNoDelete',
  cmr_closed: 'errors.cmrClosed',
  no_fields_to_update: 'errors.noFieldsToUpdate',
  cmr_signed_no_edit: 'errors.cmrSignedNoEdit',
  not_assigned_to_cmr: 'errors.notAssignedToCmr',
  trailer_active_trip_only: 'errors.trailerActiveTripOnly',
  cmr_completed_no_edit: 'errors.cmrCompletedNoEdit',
  invalid_signature_role: 'errors.invalidSignatureRole',
  signer_name_required: 'errors.signerNameRequired',
  cmr_closed_no_sign: 'errors.cmrClosedNoSign',
  no_sender_email: 'errors.noSenderEmail',
  no_receiver_email: 'errors.noReceiverEmail',
  invalid_recipient: 'errors.invalidRecipient',
  select_recipient: 'errors.selectRecipient',
  no_emails_available: 'errors.noEmailsAvailable',
  file_too_large: 'errors.fileTooLarge',
  attachment_not_found: 'errors.attachmentNotFound',
  invalid_attachment: 'errors.invalidAttachment',
  attachment_convert_failed: 'errors.attachmentConvertFailed',

  // Fustbon
  fustbon_not_found: 'errors.fustbonNotFound',
  fustbon_email_failed: 'errors.fustbonEmailFailed',

  // Customers
  customer_not_found: 'errors.customerNotFound',

  // Drivers
  no_subscription_drivers: 'errors.noSubscriptionDrivers',
  driver_limit_reached: 'errors.driverLimitReached',
  email_in_use: 'errors.emailInUse',
  no_subscription: 'errors.noSubscription',
  driver_has_account: 'errors.driverHasAccount',
  driver_no_email: 'errors.driverNoEmail',
  email_service_error: 'errors.emailServiceError',
  invalid_invitation: 'errors.invalidInvitation',
  invitation_expired: 'errors.invitationExpired',
  token_password_required: 'errors.tokenPasswordRequired',
  driver_no_account: 'errors.driverNoAccount',
  no_changes: 'errors.noChanges',
  drivers_role_only: 'errors.driversRoleOnly',

  // Uploads
  invalid_file_type: 'errors.invalid_file_type',
  file_not_found: 'errors.file_not_found',
  file_access_denied: 'errors.file_access_denied',
  photo_not_found: 'errors.photo_not_found',
  assignment_not_found: 'errors.assignment_not_found',

  // Admin
  cannot_delete_super_admin: 'errors.cannot_delete_super_admin',

  // Subscriptions
  stripe_unavailable: 'errors.stripe_unavailable',
  invalid_subscription: 'errors.invalid_subscription',
  no_active_subscription: 'errors.no_active_subscription',

  // Company
  no_company_linked: 'errors.no_company_linked',

  // Saved Locations
  name_required: 'errors.name_required',
  location_name_exists: 'errors.location_name_exists',
  location_not_found: 'errors.location_not_found',
};

/**
  driver_record_not_found: 'errors.driverRecordNotFound',
};
 * Translates a backend error key using i18n.
 * @param {string} errorKey - The error key from backend (e.g. "cmr_not_found")
 * @param {function} t - i18n translation function
 * @returns {string} Translated error message
 */
export const translateError = (errorKey, t) => {
  if (!errorKey || typeof errorKey !== 'string') {
    return t('errors.generic');
  }

  const i18nKey = ERROR_KEY_MAP[errorKey];
  if (i18nKey) {
    const translated = t(i18nKey);
    // If i18n returns the key itself, it means no translation exists
    if (translated !== i18nKey) {
      return translated;
    }
  }

  // Fallback: try errors.{key} directly
  const directKey = `errors.${errorKey}`;
  const directTranslation = t(directKey);
  if (directTranslation !== directKey) {
    return directTranslation;
  }

  // Last fallback: return generic error
  return t('errors.generic');
};

export default translateError;
